import { useEffect, useState } from "react";
import { createTicket, listTickets, updateTicketStatus } from "../api/support.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface Ticket {
  id: string;
  ticketNumber: string;
  clientId: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  assignedTo: string | null;
  version: number;
  createdAt: string;
  dueAt: string | null;
}

const STATUS_COLOR: Record<string, string> = {
  OPEN: "text-yellow-400 bg-yellow-900/30",
  IN_PROGRESS: "text-blue-400 bg-blue-900/30",
  PENDING_CUSTOMER: "text-purple-400 bg-purple-900/30",
  RESOLVED: "text-green-400 bg-green-900/30",
  CLOSED: "text-gray-500 bg-gray-800",
  REOPENED: "text-orange-400 bg-orange-900/30",
};

const PRIORITY_COLOR: Record<string, string> = {
  LOW: "text-gray-400",
  NORMAL: "text-blue-400",
  HIGH: "text-yellow-400",
  URGENT: "text-red-400",
};

const NEXT_STATUS: Record<string, string[]> = {
  OPEN: ["IN_PROGRESS", "RESOLVED"],
  REOPENED: ["IN_PROGRESS", "RESOLVED"],
  IN_PROGRESS: ["PENDING_CUSTOMER", "RESOLVED"],
  PENDING_CUSTOMER: ["IN_PROGRESS", "RESOLVED"],
  RESOLVED: ["CLOSED", "REOPENED"],
  CLOSED: ["REOPENED"],
};

export function SupportTicketsPage() {
  const { token } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    clientId: "",
    title: "",
    description: "",
    category: "GENERAL",
    priority: "NORMAL",
  });
  const [creating, setCreating] = useState(false);

  const reload = () => {
    setLoading(true);
    void listTickets(token, { status: statusFilter || undefined }).then((d) => {
      setTickets((d as { items: Ticket[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, [statusFilter]);

  const create = async () => {
    if (!form.clientId || !form.title || !form.description) return;
    setCreating(true);
    await createTicket(token, {
      clientId: form.clientId,
      title: form.title,
      description: form.description,
      category: form.category,
      priority: form.priority,
    });
    setShowForm(false);
    setCreating(false);
    setForm({ clientId: "", title: "", description: "", category: "GENERAL", priority: "NORMAL" });
    reload();
  };

  const changeStatus = async (t: Ticket, newStatus: string) => {
    await updateTicketStatus(token, t.id, t.version, newStatus);
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Support Tickets</h1>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm text-gray-300"
            >
              <option value="">All statuses</option>
              {["OPEN", "IN_PROGRESS", "PENDING_CUSTOMER", "RESOLVED", "CLOSED", "REOPENED"].map(
                (s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ),
              )}
            </select>
            <button
              onClick={() => setShowForm(!showForm)}
              className="rounded bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-500"
            >
              {showForm ? "Cancel" : "New Ticket"}
            </button>
          </div>
        </div>
        {showForm && (
          <div className="mb-6 space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                value={form.clientId}
                onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}
                placeholder="Client ID"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Ticket title"
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
              />
              <select
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                {["BILLING", "TECHNICAL", "GENERAL", "FEATURE_REQUEST", "BUG", "OTHER"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <select
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-300"
              >
                {["LOW", "NORMAL", "HIGH", "URGENT"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Describe the issue..."
              rows={3}
              className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white placeholder-gray-500"
            />
            <button
              onClick={() => void create()}
              disabled={creating}
              className="rounded bg-green-700 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create Ticket"}
            </button>
          </div>
        )}
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {tickets.length === 0 && <p className="text-gray-400">No tickets found.</p>}
            {tickets.map((t) => (
              <div key={t.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-gray-500">{t.ticketNumber}</span>
                      <span
                        className={`text-xs font-medium ${PRIORITY_COLOR[t.priority] ?? "text-gray-400"}`}
                      >
                        {t.priority}
                      </span>
                      <span className="rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                        {t.category}
                      </span>
                    </div>
                    <p className="mt-1 font-medium text-white">{t.title}</p>
                    <p className="mt-0.5 line-clamp-1 text-sm text-gray-400">{t.description}</p>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_COLOR[t.status] ?? "bg-gray-800 text-gray-400"}`}
                    >
                      {t.status}
                    </span>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  {(NEXT_STATUS[t.status] ?? []).map((ns) => (
                    <button
                      key={ns}
                      onClick={() => void changeStatus(t, ns)}
                      className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-300 hover:bg-gray-700"
                    >
                      → {ns.replace("_", " ")}
                    </button>
                  ))}
                  {t.dueAt && (
                    <span className="ml-auto text-xs text-gray-500">
                      Due: {new Date(t.dueAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
